import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import LogoMark from "@/components/LogoMark";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AcademyTab } from "@/components/AcademyAdmin";
import { SERVICES } from "@/lib/services";

const TABS = ["leads", "audits", "requests", "jobs", "reports", "florida", "academy", "pages", "projects", "reviews", "roofs", "content", "notifications", "settings", "apikeys", "audit"];

function ProjectsTab() {
  const empty = { title: "", slug: "", city: "", state: "", propertyType: "", scope: "", installType: "", systemKw: "", batteryKwh: "", completionDate: "", annualGenerationEstKwh: "", annualGenerationMeasuredKwh: "", financialResults: "", testimonial: "", testimonialPermission: false, photoKeys: [], published: false, missingAssets: "" };
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data.projects)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function save() {
    setStatus("");
    const payload = {
      ...form,
      systemKw: form.systemKw === "" ? null : Number(form.systemKw),
      batteryKwh: form.batteryKwh === "" ? null : Number(form.batteryKwh),
      annualGenerationEstKwh: form.annualGenerationEstKwh === "" ? null : Number(form.annualGenerationEstKwh),
      annualGenerationMeasuredKwh: form.annualGenerationMeasuredKwh === "" ? null : Number(form.annualGenerationMeasuredKwh),
    };
    try {
      if (projects.some((p) => p.slug === (form.slug || payload.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")))) {
        await api.patch(`/admin/projects/${form.slug || payload.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, payload);
      } else {
        await api.post("/admin/projects", payload);
      }
      setStatus("Saved." + (payload.published ? "" : " (Unpublished until you mark it published)"));
      setForm(empty);
      load();
    } catch (err) {
      setStatus(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function uploadPhoto(slug, file) {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/admin/projects/photo?slug=${encodeURIComponent(slug)}`, fd).catch(() => {});
    load();
  }

  return (
    <div data-testid="admin-projects">
      <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Case studies stay unpublished until you mark them published. List anything still missing (photos, permissions, results) in “Missing assets” — it appears in your handoff, never on the site.
      </p>
      <div className="split2">
        <div className="panel">
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>New / update case study</h3>
          <div className="twofields">
            {field("Title", <input data-testid="project-title" value={form.title} onChange={set("title")} />)}
            {field("Slug (optional)", <input data-testid="project-slug" value={form.slug} onChange={set("slug")} />)}
          </div>
          <div className="twofields">
            {field("City", <input value={form.city} onChange={set("city")} />)}
            {field("State", <input value={form.state} onChange={set("state")} />)}
          </div>
          <div className="twofields">
            {field("Property type", <input value={form.propertyType} onChange={set("propertyType")} />)}
            {field("Installation type", <input value={form.installType} onChange={set("installType")} />)}
          </div>
          {field("Scope", <textarea rows={2} value={form.scope} onChange={set("scope")} />)}
          <div className="grid13">
            {field("System size (kW)", <input type="number" value={form.systemKw} onChange={set("systemKw")} />)}
            {field("Battery (kWh, optional)", <input type="number" value={form.batteryKwh} onChange={set("batteryKwh")} />)}
            {field("Completion date", <input placeholder="2026-05" value={form.completionDate} onChange={set("completionDate")} />)}
          </div>
          <div className="grid13">
            {field("Est. annual generation (kWh)", <input type="number" value={form.annualGenerationEstKwh} onChange={set("annualGenerationEstKwh")} />)}
            {field("Measured annual generation (kWh)", <input type="number" value={form.annualGenerationMeasuredKwh} onChange={set("annualGenerationMeasuredKwh")} />)}
            {field("Documented financial results", <input value={form.financialResults} onChange={set("financialResults")} />)}
          </div>
          {field("Customer testimonial (only with written permission)", <textarea rows={2} value={form.testimonial} onChange={set("testimonial")} />)}
          <label className="check"><input type="checkbox" checked={form.testimonialPermission} onChange={set("testimonialPermission")} />Permission to publish this testimonial is on file</label>
          {field("Missing assets / permissions", <textarea rows={2} value={form.missingAssets} onChange={set("missingAssets")} />)}
          <label className="check"><input type="checkbox" checked={form.published} onChange={set("published")} data-testid="project-published" />Published (visible on the site)</label>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
            <button className="btn small" data-testid="project-save" onClick={save}>Save case study</button>
            <span className="status">{status}</span>
          </div>
        </div>
        <div className="panel">
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>All case studies ({projects.length})</h3>
          {projects.map((p) => (
            <div key={p.slug} style={{ borderBottom: "1px solid var(--line)", padding: "10px 0", fontSize: 12 }}>
              <b>{p.title}</b> <span className={`pill ${p.published ? "sent" : "no_recipients"}`}>{p.published ? "published" : "unpublished"}</span>
              <br />
              <span className="muted">{p.city}, {p.state} · {p.systemKw ? `${p.systemKw} kW` : "size TBD"} · photos: {(p.photoKeys || []).length}</span>
              <br />
              <span className="muted">Missing: {p.missingAssets || "—"}</span>
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <label className="textbutton" style={{ cursor: "pointer" }}>
                  + Photo<input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={(e) => e.target.files[0] && uploadPhoto(p.slug, e.target.files[0])} />
                </label>
                <button className="textbutton" onClick={async () => { await api.delete(`/admin/projects/${p.slug}`).catch(() => {}); load(); }}>Delete</button>
              </div>
            </div>
          ))}
          {!projects.length && <p className="muted" style={{ fontSize: 12 }}>No case studies yet. Nothing is published until you add verified projects.</p>}
        </div>
      </div>
    </div>
  );
}

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["leads:read"]);
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");
  const ALL = ["leads:read", "leads:write", "files:read", "content:read"];

  const load = useCallback(async () => {
    try { setKeys((await api.get("/admin/api-keys")).data.keys); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function createKey() {
    setErr(""); setCreated(null);
    try {
      const res = await api.post("/admin/api-keys", { name, scopes });
      setCreated(res.data);
      setName("");
      load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  async function revoke(keyId) {
    if (!window.confirm("Revoke this key? Agents using it stop working immediately.")) return;
    await api.post(`/admin/api-keys/${keyId}/revoke`);
    load();
  }

  return (
    <div data-testid="admin-apikeys" className="card">
      <h3>Private API keys (sales agents)</h3>
      <p className="muted">Keys call <code>/api/v1</code> endpoints with <code>Authorization: Bearer</code>. A new key is shown once — copy it immediately.</p>
      <div className="row">
        {field("Key name", <input data-testid="apikey-name" placeholder="e.g. Lead Qualification agent" value={name} onChange={(e) => setName(e.target.value)} />)}
        <div className="checks">
          {ALL.map((s) => (
            <label key={s} className="check">
              <input type="checkbox" data-testid={`apikey-scope-${s.replace(":", "-")}`}
                checked={scopes.includes(s)}
                onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
              {s}
            </label>
          ))}
        </div>
        <button className="btn small" data-testid="apikey-create" onClick={createKey} disabled={!name || !scopes.length}>Create key</button>
      </div>
      {err && <p className="error" data-testid="apikey-error">{err}</p>}
      {created && (
        <div className="key-reveal" data-testid="apikey-reveal">
          <strong>{created.name}</strong>
          <code data-testid="apikey-value">{created.key}</code>
          <button className="btn small" data-testid="apikey-copy" onClick={() => navigator.clipboard?.writeText(created.key)}>Copy</button>
          <p className="muted">Shown only once. Store it in the agent's config now.</p>
        </div>
      )}
      <table className="table">
        <thead><tr><th>Name</th><th>Key</th><th>Scopes</th><th>Status</th><th>Last used</th><th></th></tr></thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.keyId} data-testid="apikey-row">
              <td>{k.name}</td>
              <td><code>••••{k.last4}</code></td>
              <td>{k.scopes.join(", ")}</td>
              <td>{k.status === "active" ? "active" : `revoked`}</td>
              <td>{k.lastUsedAt ? k.lastUsedAt.slice(0, 16).replace("T", " ") : "never"}</td>
              <td>{k.status === "active" && <button className="btn small danger" data-testid="apikey-revoke" onClick={() => revoke(k.keyId)}>Revoke</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ quote: "", author: "", role: "", permissionStatus: "not_requested", originalUrl: "", published: false });
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data.reviews)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  async function save() {
    setStatus("");
    try {
      await api.post("/admin/reviews", form);
      setStatus("Saved." + (form.published ? "" : " (Unpublished until you mark it published)"));
      setForm({ quote: "", author: "", role: "", permissionStatus: "not_requested", originalUrl: "", published: false });
      load();
    } catch (err) {
      setStatus(formatApiErrorDetail(err.response?.data?.detail));
    }
  }
  return (
    <div data-testid="admin-reviews" className="split2">
      <div className="panel">
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>Add approved review</h3>
        {field("Quote", <textarea rows={3} value={form.quote} onChange={set("quote")} />)}
        <div className="twofields">
          {field("Author", <input value={form.author} onChange={set("author")} />)}
          {field("Role / company", <input value={form.role} onChange={set("role")} />)}
        </div>
        {field("Permission status", <select value={form.permissionStatus} onChange={set("permissionStatus")}>
          <option value="not_requested">not requested</option><option value="granted">granted</option><option value="declined">declined</option>
        </select>)}
        {field("Original review link (optional)", <input value={form.originalUrl} onChange={set("originalUrl")} />)}
        <label className="check"><input type="checkbox" checked={form.published} onChange={set("published")} data-testid="review-published" />Published (visible on the site)</label>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
          <button className="btn small" data-testid="review-save" onClick={save}>Save review</button>
          <span className="status">{status}</span>
        </div>
        <p className="micro">Only authentic reviews with permission are shown publicly. Never invent quotes or ratings.</p>
      </div>
      <div className="panel">
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>All reviews ({reviews.length})</h3>
        {reviews.map((r) => (
          <div key={r.id} style={{ borderBottom: "1px solid var(--line)", padding: "10px 0", fontSize: 12 }}>
            <b>{r.author}</b> <span className={`pill ${r.published ? "sent" : "no_recipients"}`}>{r.published ? "published" : "unpublished"}</span>
            <br /><span className="muted">“{r.quote.slice(0, 90)}{r.quote.length > 90 ? "…" : ""}” · permission: {r.permissionStatus}</span>
            <br /><button className="textbutton" onClick={async () => { await api.delete(`/admin/reviews/${r.id}`).catch(() => {}); load(); }}>Delete</button>
          </div>
        ))}
        {!reviews.length && <p className="muted" style={{ fontSize: 12 }}>No reviews saved yet.</p>}
      </div>
    </div>
  );
}

function ContentTab() {
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState({ followUpNote: "", responseTimeNote: "", assumptionSources: "" });
  const [saved, setSaved] = useState("");
  useEffect(() => {
    api.get("/admin/content").then((r) => setC(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setNotes({ followUpNote: r.data.followUpNote || "", responseTimeNote: r.data.responseTimeNote || "", assumptionSources: r.data.assumptionSources || "" })).catch(() => {});
  }, []);
  if (!c) return <p className="muted">Loading…</p>;
  const setHero = (k) => (e) => setC((cur) => ({ ...cur, hero: { ...cur.hero, [k]: e.target.value } }));
  const setAbout = (k) => (e) => setC((cur) => ({ ...cur, about: { ...cur.about, [k]: e.target.value } }));
  const setNote = (k) => (e) => setNotes((cur) => ({ ...cur, [k]: e.target.value }));
  return (
    <div data-testid="admin-content">
      <div className="split2">
        <div className="panel">
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>Homepage hero</h3>
          {field("Headline", <textarea rows={2} data-testid="hero-headline" value={c.hero?.headline || ""} onChange={setHero("headline")} />)}
          {field("Supporting copy", <textarea rows={3} value={c.hero?.sub || ""} onChange={setHero("sub")} />)}
          <div className="twofields">
            {field("Primary CTA label", <input value={c.hero?.ctaPrimary || ""} onChange={setHero("ctaPrimary")} />)}
            {field("Secondary CTA label", <input value={c.hero?.ctaSecondary || ""} onChange={setHero("ctaSecondary")} />)}
          </div>
          <h3 style={{ fontSize: 16, margin: "18px 0 8px" }}>Company identity (About page)</h3>
          {field("Who we are", <textarea rows={2} value={c.about?.intro || ""} onChange={setAbout("intro")} />)}
          {field("Lawrence Williams's role", <textarea rows={2} value={c.about?.role || ""} onChange={setAbout("role")} />)}
          {field("Coastal Energy relationship", <textarea rows={3} value={c.about?.relationship || ""} onChange={setAbout("relationship")} />)}
          {field("Design / contract / install / service responsibility", <textarea rows={3} value={c.about?.responsibilities || ""} onChange={setAbout("responsibilities")} />)}
          <div className="twofields">
            {field("Service territories", <textarea rows={2} value={c.about?.territories || ""} onChange={setAbout("territories")} />)}
            {field("Credentials & licenses", <textarea rows={2} value={c.about?.credentials || ""} onChange={setAbout("credentials")} />)}
          </div>
          {field("Warranties", <textarea rows={2} value={c.about?.warranties || ""} onChange={setAbout("warranties")} />)}
          <button className="btn small" data-testid="content-save" onClick={async () => { setSaved(""); await api.put("/admin/content", { hero: c.hero, about: c.about, commercialFaqs: c.commercialFaqs || [] }).then(() => setSaved("Saved.")).catch((e) => setSaved(formatApiErrorDetail(e.response?.data?.detail))); }}>
            Save content
          </button>
        </div>
        <div className="panel">
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>Process wording & financial sources</h3>
          {field("Follow-up note (shown beside upload forms)", <textarea rows={3} data-testid="followup-note" value={notes.followUpNote} onChange={setNote("followUpNote")} />)}
          {field("Response-time note (only commit to what the team can meet)", <input data-testid="response-time-note" value={notes.responseTimeNote} onChange={setNote("responseTimeNote")} />)}
          {field("Financial assumption sources & review date", <textarea rows={4} value={notes.assumptionSources} onChange={setNote("assumptionSources")} />)}
          <button className="btn small" data-testid="notes-save" onClick={async () => { setSaved(""); await api.put("/admin/notes", notes).then(() => setSaved("Saved.")).catch((e) => setSaved(formatApiErrorDetail(e.response?.data?.detail))); }}>
            Save wording
          </button>
          <h3 style={{ fontSize: 16, margin: "18px 0 8px" }}>Commercial FAQ (overrides built-in list)</h3>
          <p className="micro">Leave empty to use the built-in 12-question FAQ. Format one question per block via the site defaults until you need custom wording.</p>
        </div>
      </div>
      <span className="status">{saved}</span>
    </div>
  );
}

function field(label, control) {
  return (
    <label className="field">
      {label}
      {control}
    </label>
  );
}

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/login", { email, password }).catch((err) => {
        const detail = err.response?.data?.detail;
        throw new Error(formatApiErrorDetail(detail));
      });
      onAuthed();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} data-testid="admin-login-form" style={{ maxWidth: 380, margin: "60px auto" }}>
      <div className="brand" style={{ marginBottom: 22 }}>
        <span className="brandmark"><LogoMark size={22} /></span>
        <span>SOLAR BLACK BELT<small>ADMIN</small></span>
      </div>
      {field("Email", <input type="email" required data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} />)}
      {field("Password", <input type="password" required data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} />)}
      <button className="btn" style={{ width: "100%", marginTop: 20 }} disabled={busy} data-testid="admin-login-submit">
        {busy ? "Checking…" : "Sign in"} <span>↗</span>
      </button>
      {error && <p className="status" role="alert" style={{ color: "#a33a17" }} data-testid="admin-login-error">{error}</p>}
      <p className="micro">Server-verified sessions only. Lockout applies after repeated failures.</p>
    </form>
  );
}

function LeadsTab({ leads, q, setQ, reload }) {
  const [selected, setSelected] = useState("");
  async function setStatus(reference, status) {
    if (!status) return;
    await api.patch(`/admin/leads/${reference}`, { status }).catch(() => {});
    reload();
  }
  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input placeholder="Search name, email, reference, address" data-testid="admin-lead-search" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1, minWidth: 220, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, background: "white" }} />
        <a className="btn light" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/leads.csv`} target="_blank" rel="noreferrer" data-testid="admin-export-csv">Export CSV ↗</a>
      </div>
      <table className="admin-table" data-testid="admin-leads-table">
        <thead>
          <tr><th>Reference</th><th>Service</th><th>Contact</th><th>Bill</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.reference}>
              <td><Link to={`/admin/leads/${l.reference}`} style={{ color: "var(--teal)", fontWeight: 800 }}>{l.reference}</Link></td>
              <td>{l.contextService || l.serviceId}</td>
              <td>{l.name}<br /><span className="muted" style={{ fontSize: 10 }}>{l.email}</span></td>
              <td>{l.monthlyBill ? `$${Number(l.monthlyBill).toLocaleString()}` : "—"}</td>
              <td>
                <select value={l.status} data-testid={`lead-status-${l.reference}`} onChange={(e) => { setSelected(l.reference); setStatus(l.reference, e.target.value); }} style={{ padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 6, background: "white", fontSize: 11 }}>
                  {["new", "contacted", "qualified", "assessment", "closed", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td><Link to={`/admin/leads/${l.reference}`} className="textbutton">Open ↗</Link></td>
            </tr>
          ))}
          {!leads.length && <tr><td colSpan={6}>No leads match. New submissions appear here instantly.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function LeadDetail({ reference }) {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/leads/${reference}`).then((r) => setLead(r.data)).catch(() => setLead(null));
  }, [reference]);
  useEffect(load, [load]);

  if (!lead) return <p className="muted">Loading…</p>;
  const storage = lead.storageInterest || {};
  const dims = lead.dimensions || {};
  return (
    <div data-testid="admin-lead-detail">
      <Link to="/admin" className="textbutton">← All leads</Link>
      <h2 style={{ margin: "14px 0 4px" }}>{lead.reference}</h2>
      <p className="muted" style={{ fontSize: 12, marginBottom: 18 }}>{lead.contextService || lead.serviceId} · {lead.audience} · created {new Date(lead.createdAt).toLocaleString()}</p>
      <div className="split2">
        <div className="panel">
          <h3 style={{ fontSize: 16, marginBottom: 10 }}>Project record</h3>
          <table className="admin-table">
            <tbody>
              <tr><th>Contact</th><td>{lead.name} · {lead.email} · {lead.phone}</td></tr>
              <tr><th>Property</th><td>{lead.address || "—"}{lead.confirmedBuilding?.name ? ` (building: ${lead.confirmedBuilding.name})` : ""}</td></tr>
              <tr><th>Electric use</th><td>{lead.monthlyBill ? `$${Number(lead.monthlyBill).toLocaleString()}/mo` : "—"} · {lead.utility || "utility not given"}</td></tr>
              <tr><th>Roof</th><td>{dims.length ? `${dims.length} × ${dims.width} ${dims.units}, concept ${dims.coverage}% · mode ${lead.layoutSourceMode || "n/a"}` : "—"}</td></tr>
              <tr><th>Storage</th><td>{storage.goal || "—"}{storage.units ? ` · ${storage.units} unit concept` : ""}</td></tr>
              <tr><th>Answer</th><td>{lead.serviceAnswer || "—"}</td></tr>
              <tr><th>Priorities</th><td>{(lead.priorities || []).join(", ") || "—"}</td></tr>
              <tr><th>Consent</th><td>{lead.contactConsent?.granted ? "contact yes" : "none"} · marketing {lead.marketingConsent ? "yes" : "no"} · policy {lead.contactConsent?.policyVersion}</td></tr>
              <tr><th>Call/text consent</th><td data-testid="admin-lead-sms-consent">{lead.smsConsent ? `${lead.smsConsent.granted ? "CHECKED" : "unchecked"} — “${lead.smsConsent.text || ""}”` : "not recorded (older submission)"}</td></tr>
              <tr><th>Source page</th><td>{lead.attribution?.page || lead.source || "-"}</td></tr>
              <tr><th>Assigned to</th><td>{lead.salesAssignee || "Commercial sales"}</td></tr>
              <tr><th>Source</th><td>{[lead.attribution?.page, lead.attribution?.landingPage && `landing: ${lead.attribution.landingPage}`, lead.attribution?.utm_source && `utm: ${lead.attribution.utm_source}${lead.attribution.utm_medium ? `/${lead.attribution.utm_medium}` : ""}`].filter(Boolean).join(" · ") || "—"}</td></tr>
              <tr><th>Status history</th><td>{(lead.statusHistory || []).map((h) => `${h.status} (${new Date(h.at).toLocaleDateString()})`).join(" · ")}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <div className="panel" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, marginBottom: 10 }}>Private files</h3>
            {(lead.uploads || []).length ? (
              lead.uploads.map((u) => (
                <p key={u.id} style={{ fontSize: 12 }}>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${reference}/files/${u.id}`} target="_blank" rel="noreferrer" data-testid={`lead-file-${u.id}`}>{u.name}</a>{" "}
                  <span className="muted">({u.kind}, {(u.size / 1048576).toFixed(1)} MB)</span>
                </p>
              ))
            ) : (
              <p className="muted" style={{ fontSize: 12 }}>No files attached. Files open for admins only — never public.</p>
            )}
          </div>
          <div className="panel" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, marginBottom: 10 }}>Notifications</h3>
            {(lead.notifications || []).map((n) => (
              <p key={n.jobId} style={{ fontSize: 12 }}>
                <span className={`pill ${n.status}`}>{n.status}</span> {new Date(n.createdAt).toLocaleString()} {n.lastError ? `· ${n.lastError}` : ""}
              </p>
            ))}
            {!(lead.notifications || []).length && <p className="muted" style={{ fontSize: 12 }}>None queued.</p>}
          </div>
          <div className="panel">
            <h3 style={{ fontSize: 16, marginBottom: 10 }}>Notes</h3>
            {(lead.notes || []).map((n, i) => (
              <p key={i} style={{ fontSize: 12 }}><b>{n.author}</b> · {new Date(n.at).toLocaleString()}<br />{n.text}</p>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input placeholder="Add a note…" data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8 }} />
              <button className="btn small" data-testid="lead-note-save" onClick={async () => { if (!note.trim()) return; await api.patch(`/admin/leads/${reference}`, { note: note.trim() }); setNote(""); load(); }}>Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationsTab({ reload }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/outbox").then((r) => setRows(r.data.notifications)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  return (
    <div data-testid="admin-notifications">
      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <button className="btn small" data-testid="admin-test-email" onClick={async () => { await api.post("/admin/test-email").catch(() => {}); reload(); load(); }}>
          Send test notification (to my own inbox)
        </button>
      </div>
      <table className="admin-table">
        <thead><tr><th>When</th><th>Subject</th><th>Status</th><th>Attempts</th><th>Recipients</th><th></th></tr></thead>
        <tbody>
          {rows.map((n) => (
            <tr key={n.jobId}>
              <td>{new Date(n.createdAt).toLocaleString()}</td>
              <td>{n.subject}</td>
              <td><span className={`pill ${n.status}`}>{n.status}</span>{n.lastError ? <><br /><span className="muted" style={{ fontSize: 10 }}>{n.lastError}</span></> : null}</td>
              <td>{n.attempts}</td>
              <td>{(n.recipients || []).join(", ") || "—"}</td>
              <td>
                {n.status !== "sent" && (
                  <button className="textbutton" data-testid={`outbox-retry-${n.jobId}`} onClick={async () => { await api.post(`/admin/outbox/${n.jobId}/retry`).catch(() => {}); load(); }}>
                    Retry ↗
                  </button>
                )}
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={6}>No notifications yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab({ reload }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setS(r.data);
      setRecipientsText((r.data.recipients || []).join("\n"));
    }).catch(() => {});
  }, []);
  if (!s) return <p className="muted">Loading…</p>;
  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));
  return (
    <div data-testid="admin-settings">
      <div className="grid13">
        {field("Public brand", <input data-testid="settings-brand" value={s.brand} onChange={(e) => set("brand", e.target.value)} />)}
        {field("Brand line", <input data-testid="settings-brandline" value={s.brandLine} onChange={(e) => set("brandLine", e.target.value)} />)}
        {field("Phone", <input data-testid="settings-phone" value={s.phone} onChange={(e) => set("phone", e.target.value)} />)}
      </div>
      <div className="grid13">
        {field("Phone link (href)", <input data-testid="settings-phonehref" value={s.phoneHref} onChange={(e) => set("phoneHref", e.target.value)} />)}
        {field("Notification subject prefix", <input data-testid="settings-prefix" value={s.notificationPrefix} onChange={(e) => set("notificationPrefix", e.target.value)} />)}
        {field("Reply-to email", <input data-testid="settings-replyto" value={s.replyTo || ""} onChange={(e) => set("replyTo", e.target.value)} />)}
      </div>
      {field("Tagline", <input data-testid="settings-tagline" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} />)}
      {field("Service coverage note (public)", <textarea rows={2} data-testid="settings-coverage" value={s.coverageNote} onChange={(e) => set("coverageNote", e.target.value)} />)}
      {field("Operator identity (privacy notice)", <textarea rows={2} data-testid="settings-operator" value={s.operatorIdentity} onChange={(e) => set("operatorIdentity", e.target.value)} />)}
      {field("Notification recipients — one per line. The team sees nothing until at least one address is saved.", <textarea rows={4} data-testid="settings-recipients" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} />)}
      <fieldset style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 16, margin: "16px 0" }}>
        <legend style={{ fontSize: 11, fontWeight: 800, padding: "0 8px" }}>ENABLED SERVICES</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {SERVICES.map((svc) => (
            <label key={svc.id} className="check" style={{ margin: 0 }}>
              <input
                type="checkbox"
                data-testid={`settings-service-${svc.id}`}
                checked={s.enabledServices.includes(svc.id)}
                onChange={(e) => set("enabledServices", e.target.checked ? [...s.enabledServices, svc.id] : s.enabledServices.filter((x) => x !== svc.id))}
              />
              <span>{svc.name}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 16, margin: "16px 0" }}>
        <legend style={{ fontSize: 11, fontWeight: 800, padding: "0 8px" }}>PER-SERVICE EMAIL ROUTING (OPTIONAL)</legend>
        <div className="grid13">
          {SERVICES.slice(0, 6).map((svc) => (
            <label key={svc.id} className="field" style={{ marginTop: 8 }}>
              {svc.name}
              <input
                placeholder="extra routing emails, comma separated"
                data-testid={`settings-routing-${svc.id}`}
                defaultValue={(s.perServiceRouting?.[svc.id] || []).join(", ")}
                onBlur={(e) => set("perServiceRouting", { ...s.perServiceRouting, [svc.id]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
              />
            </label>
          ))}
        </div>
      </fieldset>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <button className="btn" data-testid="settings-save" onClick={async () => {
          setSaved("");
          const payload = { ...s, recipients: recipientsText.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean) };
          await api.put("/admin/settings", payload).then(() => setSaved("Saved.")).catch((err) => setSaved(formatApiErrorDetail(err.response?.data?.detail)));
          reload();
        }}>
          Save settings
        </button>
        <span className="status" data-testid="settings-saved">{saved}</span>
      </div>
      <p className="micro">Recipients are validated, deduplicated and stored server-side. API keys never appear here — they live in deployment configuration.</p>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/audit").then((r) => setRows(r.data.entries)).catch(() => {});
  }, []);
  return (
    <table className="admin-table" data-testid="admin-audit">
      <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Details</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{new Date(r.at).toLocaleString()}</td>
            <td>{r.actor}</td>
            <td>{r.action}</td>
            <td>{r.target}</td>
            <td>{r.details}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={5}>No admin activity recorded yet.</td></tr>}
      </tbody>
    </table>
  );
}

function AuditsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/audits").then((r) => setRows(r.data.audits)).catch(() => {});
  }, []);
  return (
    <table className="admin-table" data-testid="admin-audits">
      <thead><tr><th>Audit ID</th><th>Company</th><th>Utility</th><th>Spend</th><th>Files</th><th>Status</th></tr></thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.auditId}>
            <td>{a.auditId}</td>
            <td>{a.company}<br /><span className="muted" style={{ fontSize: 10 }}>{a.email}</span></td>
            <td>{a.utility}</td>
            <td>${Number(a.monthlySpend).toLocaleString()}/mo</td>
            <td>{(a.fileIds || []).length}</td>
            <td><span className="pill queued">{a.status}</span></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6}>No bill audits yet.</td></tr>}
      </tbody>
    </table>
  );
}

function RoofLookupTab() {
  const [address, setAddress] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function lookup() {
    if (!address.trim() || busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const r = await api.post("/admin/solar-lookup", { address: address.trim() });
      setResult(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  const rows = result
    ? [
        ["Building", result.address],
        ["Location", `${result.state} ${result.postalCode}`],
        ["Roof area", result.roofAreaM2 ? `${result.roofAreaM2.toLocaleString()} m² (~${Math.round(result.roofAreaM2 * 10.764).toLocaleString()} sq ft)` : "—"],
        ["Building footprint", result.buildingFootprintM2 ? `${result.buildingFootprintM2.toLocaleString()} m²` : "—"],
        ["Max panels", result.maxArrayPanels ? `${result.maxArrayPanels} × ${result.panelCapacityWatts} W` : "—"],
        ["System potential", result.systemKwEstimate ? `~${result.systemKwEstimate.toLocaleString()} kW` : "—"],
        ["Year-1 energy", result.year1EnergyKwhEstimate ? `~${Math.round(result.year1EnergyKwhEstimate).toLocaleString()} kWh (Google sunshine-hours model)` : "—"],
        ["Sunlight", result.maxSunshineHoursPerYear ? `${result.maxSunshineHoursPerYear.toLocaleString()} sun-hours/yr` : "—"],
        ["Carbon offset", result.carbonOffsetKgPerYear ? `~${Math.round(result.carbonOffsetKgPerYear / 1000).toLocaleString()} t CO₂/yr` : "—"],
        ["Imagery", `${result.imageryDate} (processed ${result.imageryProcessedDate}, quality ${result.imageryQuality})`],
      ]
    : [];
  return (
    <div data-testid="admin-roof-lookup">
      <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Looks up a building with the Google Solar API (address geocoded server-side; the key never touches the browser).
        Results are specialist estimates for your admin conversation — verify on the map before quoting anything.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input
          placeholder="Street address, city, state"
          data-testid="solar-address-input"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
          style={{ flex: 1, minWidth: 260, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, background: "white" }}
        />
        <button className="btn small" data-testid="solar-lookup-btn" onClick={lookup} disabled={busy}>
          {busy ? "Looking up…" : "Look up roof"}
        </button>
      </div>
      {error && <p className="status" role="alert" style={{ color: "#a33a17", marginTop: 12 }} data-testid="solar-lookup-error">{error}</p>}
      {result && (
        <div style={{ marginTop: 16 }}>
          <table className="admin-table" data-testid="solar-result">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}><th style={{ width: 200 }}>{k}</th><td>{v}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center" }}>
            <a className="btn light" href={result.mapsLink} target="_blank" rel="noreferrer" data-testid="solar-maps-link">
              Open in Google Maps ↗
            </a>
            <span className="muted" style={{ fontSize: 11 }}>Analyses available: {result.financialAnalysesCount} · estimates only — a CPA confirms incentives, an engineer confirms the roof.</span>
          </div>
        </div>
      )}
      {!result && !error && <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>No lookups yet this session. Try a full street address with city and state.</p>}
    </div>
  );
}

function ServiceRequestsTab() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  useEffect(() => {
    api.get(`/admin/service-requests${statusFilter ? `?status=${statusFilter}` : ""}`).then((r) => setRows(r.data.requests)).catch(() => {});
  }, [statusFilter]);
  return (
    <div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 12 }}>
        Status
        <select value={statusFilter} data-testid="sreq-status-filter" onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          {["received", "contacted", "qualified", "quoted", "scheduled", "won", "lost", "spam"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </label>
      <table className="admin-table" data-testid="admin-service-requests">
        <thead><tr><th>Reference</th><th>Service</th><th>Contact</th><th>Property</th><th>Files</th><th>Status</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reference}>
              <td>{r.reference}<br /><span className="muted" style={{ fontSize: 10 }}>{(r.createdAt || "").slice(0, 10)}</span></td>
              <td>{r.serviceName}{r.configuration ? <><br /><span className="muted" style={{ fontSize: 10 }}>{r.configuration}</span></> : null}</td>
              <td>{r.name}<br /><span className="muted" style={{ fontSize: 10 }}>{r.email} · {r.phone || "-"} · {r.contactMethod}</span></td>
              <td>{r.address}<br /><span className="muted" style={{ fontSize: 10 }}>{r.audience} · {r.authority}</span></td>
              <td>{(r.uploadIds || []).length}</td>
              <td><span className="pill queued">{r.status}</span></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={6}>No service requests yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function JobApplicationsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/job-applications").then((r) => setRows(r.data.applications)).catch(() => {});
  }, []);
  return (
    <table className="admin-table" data-testid="admin-job-applications">
      <thead><tr><th>Reference</th><th>Position</th><th>Applicant</th><th>Experience</th><th>Resume</th><th>Status</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.reference}>
            <td>{r.reference}<br /><span className="muted" style={{ fontSize: 10 }}>{(r.createdAt || "").slice(0, 10)}</span></td>
            <td>{r.position}</td>
            <td>{r.name}<br /><span className="muted" style={{ fontSize: 10 }}>{r.email} · {r.phone}</span></td>
            <td style={{ maxWidth: 260 }}>{(r.experience || "-").slice(0, 120)}{(r.experience || "").length > 120 ? "…" : ""}</td>
            <td>
              {r.resumeId ? (
                <a href={`/api/admin/job-applications/${r.reference}/resume/${r.resumeId}`} target="_blank" rel="noreferrer" data-testid={`job-resume-link-${r.reference}`}>View resume ↗</a>
              ) : "—"}
            </td>
            <td><span className="pill queued">{r.status}</span></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={6}>No job applications yet.</td></tr>}
      </tbody>
    </table>
  );
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    api.get("/admin/service-content").then((r) => {
      setPages(r.data.pages);
      if (!selectedId && r.data.pages.length) setSelectedId(r.data.pages[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const page = pages.find((p) => p.id === selectedId);
    if (page) setDraft(JSON.parse(JSON.stringify(page)));
  }, [selectedId]); // eslint-disable-line

  async function save() {
    if (!draft) return;
    try {
      const payload = {
        name: draft.name, hero: draft.hero, status: draft.status, privateNote: draft.privateNote || "",
        pricingRows: draft.pricingRows || [], faqs: draft.faqs || [],
      };
      const r = await api.put(`/admin/service-content/${draft.id}`, payload);
      setDraft(r.data);
      setPages((p) => p.map((x) => (x.id === r.data.id ? r.data : x)));
      setSaved("Saved ✓");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setSaved(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    }
  }
  const updRow = (i, key, value) => setDraft((d) => ({ ...d, pricingRows: d.pricingRows.map((r, x) => (x === i ? { ...r, [key]: value } : r)) }));
  const updFaq = (i, key, value) => setDraft((d) => ({ ...d, faqs: d.faqs.map((f, x) => (x === i ? { ...f, [key]: value } : f)) }));

  if (!draft) return <p className="muted" style={{ fontSize: 12 }}>Loading pages…</p>;
  return (
    <div data-testid="admin-pages-tab">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <select value={selectedId} data-testid="pages-select" onChange={(e) => setSelectedId(e.target.value)}>
          {pages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.status === "draft" ? " (draft)" : ""}</option>)}
        </select>
        <button className="btn small" data-testid="pages-save" onClick={save}>Save changes</button>
        {saved && <span style={{ fontSize: 12, fontWeight: 800, color: "var(--teal)" }} data-testid="pages-saved">{saved}</span>}
      </div>
      <label style={{ display: "block", fontSize: 12, marginBottom: 10, maxWidth: 560 }}>
        Hero line
        <input style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8 }} maxLength={300} data-testid="pages-hero" value={draft.hero || ""} onChange={(e) => setDraft({ ...draft, hero: e.target.value })} />
      </label>
      <div style={{ display: "flex", gap: 18, marginBottom: 14, fontSize: 12, flexWrap: "wrap" }}>
        <label>Status
          <select value={draft.status} data-testid="pages-status" onChange={(e) => setDraft({ ...draft, status: e.target.value })} style={{ marginLeft: 8 }}>
            <option value="published">published</option>
            <option value="draft">draft (hidden from public)</option>
          </select>
        </label>
      </div>
      <label style={{ display: "block", fontSize: 12, marginBottom: 14, maxWidth: 560 }}>
        Private verification notes (admin-only, never published)
        <textarea rows={2} style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8 }} maxLength={1000} data-testid="pages-note" value={draft.privateNote || ""} onChange={(e) => setDraft({ ...draft, privateNote: e.target.value })} />
      </label>
      <h4 style={{ margin: "10px 0" }}>Pricing rows</h4>
      <table className="admin-table" data-testid="pages-pricing">
        <thead><tr><th>Item</th><th>Amount</th><th>Status</th><th>Conditions</th></tr></thead>
        <tbody>
          {(draft.pricingRows || []).map((row, i) => (
            <tr key={i}>
              <td><input value={row.item} onChange={(e) => updRow(i, "item", e.target.value)} /></td>
              <td><input value={row.amount} onChange={(e) => updRow(i, "amount", e.target.value)} /></td>
              <td>
                <select value={row.status} onChange={(e) => updRow(i, "status", e.target.value)}>
                  {["starting", "typical", "illustrative", "member-only", "quote-required", "fixed", "free", "upgrade", "historical", "reference", "included"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td><input value={row.conditions} onChange={(e) => updRow(i, "conditions", e.target.value)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4 style={{ margin: "14px 0" }}>FAQs</h4>
      {(draft.faqs || []).map((f, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <input style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, fontWeight: 700 }} value={f.q} data-testid={`pages-faq-q-${i + 1}`} onChange={(e) => updFaq(i, "q", e.target.value)} />
          <textarea rows={2} style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, marginTop: 4 }} value={f.a} data-testid={`pages-faq-a-${i + 1}`} onChange={(e) => updFaq(i, "a", e.target.value)} />
        </div>
      ))}
    </div>
  );
}

function ReportsTab() {
  const [rows, setRows] = useState([]);
  const [states, setStates] = useState([]);
  const load = () => api.get("/admin/report-jobs").then((r) => { setRows(r.data.jobs); setStates(r.data.states); }).catch(() => {});
  useEffect(() => { load(); }, []);
  async function advance(reference, status) {
    if (!status || status === "choose…") return;
    await api.put(`/admin/report-jobs/${reference}`, { status }).catch(() => {});
    load();
  }
  return (
    <div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>Preliminary 25-year model reports created when a bill audit arrives with a Florida estimator scenario. Lifecycle: queued → modeling → needs_confirmation → ready → delivered.</p>
      <table className="admin-table" data-testid="admin-report-jobs">
        <thead><tr><th>Reference</th><th>Audit</th><th>Scenario</th><th>Status</th><th>Advance</th></tr></thead>
        <tbody>
          {rows.map((j) => (
            <tr key={j.reference}>
              <td>{j.reference}<br /><span className="muted" style={{ fontSize: 10 }}>{(j.createdAt || "").slice(0, 10)}</span></td>
              <td>{j.auditId}</td>
              <td style={{ fontSize: 11 }}>
                {j.scenario?.systemKW ? `${j.scenario.systemKW} kW · ` : ""}
                {j.scenario?.utility || "-"} · {j.scenario?.roofType || "-"}<br />
                <span className="muted">NPV {j.scenario?.npv25 != null ? `$${Math.round(j.scenario.npv25).toLocaleString()}` : "-"} · payback {j.scenario?.paybackYears != null ? `${j.scenario.paybackYears}y` : "-"} · from {j.scenario?.originPath || "-"}</span>
              </td>
              <td><span className="pill queued">{j.status}</span></td>
              <td>
                <select defaultValue="" data-testid={`report-advance-${j.reference}`} onChange={(e) => advance(j.reference, e.target.value)}>
                  <option value="">choose…</option>
                  {states.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5}>No report jobs yet — they are created when a bill audit arrives with a Florida estimator scenario attached.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FloridaTab() {
  const [pages, setPages] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [queue, setQueue] = useState([]);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    api.get("/admin/florida/pages").then((r) => {
      setPages(r.data.pages);
      setStates(r.data.states);
      if (r.data.pages.length) setSelectedId(r.data.pages[0].path);
    }).catch(() => {});
    api.get("/admin/florida/queue").then((r) => setQueue(r.data.queue)).catch(() => {});
  }, []);

  useEffect(() => {
    const page = pages.find((p) => p.path === selectedId);
    if (page) setDraft(JSON.parse(JSON.stringify(page)));
  }, [selectedId]); // eslint-disable-line

  async function save() {
    if (!draft) return;
    try {
      const r = await api.put("/admin/florida/pages/edit", {
        path: draft.path, h1: draft.h1, intro: draft.intro, status: draft.status,
        reviewedOn: draft.reviewedOn, sections: draft.sections, faqs: draft.faqs,
      });
      setDraft(r.data);
      setPages((p) => p.map((x) => (x.path === r.data.path ? r.data : x)));
      setSaved("Saved ✓");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setSaved(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    }
  }

  async function rollback() {
    if (!draft) return;
    try {
      const r = await api.post(`/admin/florida/pages${draft.path}/rollback`);
      setDraft(r.data);
      setPages((p) => p.map((x) => (x.path === r.data.path ? r.data : x)));
      setSaved("Rolled back ✓");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setSaved(formatApiErrorDetail(e.response?.data?.detail) || "Rollback failed.");
    }
  }

  const updSection = (i, key, value) => setDraft((d) => ({ ...d, sections: d.sections.map((s, x) => (x === i ? { ...s, [key]: value } : s)) }));
  const updFaq = (i, key, value) => setDraft((d) => ({ ...d, faqs: (d.faqs || []).map((f, x) => (x === i ? { ...f, [key]: value } : f)) }));
  const updQueue = (order, patch) => {
    setQueue((q) => q.map((x) => (x.order === order ? { ...x, ...patch } : x)));
    api.put(`/admin/florida/queue/${order}?status=${encodeURIComponent(patch.queueStatus || "draft_outline")}&assignedReviewer=${encodeURIComponent(patch.assignedReviewer || "")}`).catch(() => {});
  };

  if (!draft) return <p className="muted" style={{ fontSize: 12 }}>Loading Florida content…</p>;
  return (
    <div data-testid="admin-florida-tab">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
        <select value={selectedId} data-testid="florida-page-select" onChange={(e) => setSelectedId(e.target.value)}>
          {pages.map((p) => <option key={p.path} value={p.path}>{p.path}{p.status !== "published" ? ` (${p.status})` : ""}</option>)}
        </select>
        <button className="btn small" data-testid="florida-save" onClick={save}>Save (new revision)</button>
        <button className="btn light" data-testid="florida-rollback" onClick={rollback}>Rollback</button>
        {saved && <span style={{ fontSize: 12, fontWeight: 800, color: "var(--teal)" }} data-testid="florida-saved">{saved}</span>}
      </div>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 12, fontSize: 12 }}>
        <label>Status
          <select value={draft.status} data-testid="florida-status" onChange={(e) => setDraft({ ...draft, status: e.target.value })} style={{ marginLeft: 6 }}>
            {states.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label>Source check date
          <input value={draft.reviewedOn || ""} maxLength={10} data-testid="florida-reviewed" onChange={(e) => setDraft({ ...draft, reviewedOn: e.target.value })} style={{ marginLeft: 6, padding: "4px 8px", border: "1px solid var(--line)", borderRadius: 8 }} />
        </label>
        <span className="muted">Revision {draft.revision} · tax/utility edits return pages for review before publishing.</span>
      </div>
      <label style={{ display: "block", fontSize: 12, marginBottom: 10, maxWidth: 640 }}>
        H1
        <input style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8 }} value={draft.h1 || ""} data-testid="florida-h1" onChange={(e) => setDraft({ ...draft, h1: e.target.value })} />
      </label>
      <label style={{ display: "block", fontSize: 12, marginBottom: 14, maxWidth: 640 }}>
        Intro
        <textarea rows={3} style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8 }} value={draft.intro || ""} data-testid="florida-intro" onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
      </label>
      <h4 style={{ margin: "10px 0" }}>Sections</h4>
      {(draft.sections || []).map((s, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <input style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, fontWeight: 700 }} value={s.heading} data-testid={`florida-section-h-${i + 1}`} onChange={(e) => updSection(i, "heading", e.target.value)} />
          <textarea rows={3} style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, marginTop: 4 }} value={s.body} data-testid={`florida-section-b-${i + 1}`} onChange={(e) => updSection(i, "body", e.target.value)} />
        </div>
      ))}
      <h4 style={{ margin: "14px 0" }}>FAQs</h4>
      {(draft.faqs || []).map((f, i) => (
        <div key={i} style={{ marginBottom: 10 }}>
          <input style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, fontWeight: 700 }} value={f.question} data-testid={`florida-faq-q-${i + 1}`} onChange={(e) => updFaq(i, "question", e.target.value)} />
          <textarea rows={2} style={{ width: "100%", padding: "7px 9px", border: "1px solid var(--line)", borderRadius: 8, marginTop: 4 }} value={f.answer} data-testid={`florida-faq-a-${i + 1}`} onChange={(e) => updFaq(i, "answer", e.target.value)} />
        </div>
      ))}
      <h4 style={{ margin: "18px 0 10px" }}>Editorial queue</h4>
      <table className="admin-table" data-testid="admin-florida-queue">
        <thead><tr><th>Priority</th><th>Topic</th><th>Target</th><th>Status</th><th>Reviewer</th></tr></thead>
        <tbody>
          {queue.map((q) => (
            <tr key={q.order ?? q.priority}>
              <td>{q.priority}</td>
              <td style={{ maxWidth: 280 }}>{q.topic}</td>
              <td style={{ fontSize: 11 }}>{q.updateTarget}</td>
              <td>
                <select value={q.queueStatus || q.status} data-testid={`florida-queue-${q.order ?? q.priority}`} onChange={(e) => updQueue(q.order ?? q.priority, { queueStatus: e.target.value })}>
                  {["draft_outline", "in_progress", "in_review", "published", "archived"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td><input placeholder="assign…" defaultValue={q.assignedReviewer || ""} onBlur={(e) => updQueue(q.order ?? q.priority, { assignedReviewer: e.target.value })} style={{ width: 110 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="micro">Nothing auto-publishes. Tariff and tax claim edits move pages back through review; use real publication dates and an assigned reviewer before publishing.</p>
    </div>
  );
}

export default function AdminPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState(leadId ? "leads" : "leads");
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [total, setTotal] = useState(0);

  const loadLeads = useCallback(() => {
    if (!authed) return;
    api.get("/admin/leads", { params: q ? { q } : {} }).then((r) => {
      setLeads(r.data.leads);
      setTotal(r.data.total);
    }).catch(() => {});
  }, [authed, q]);
  useEffect(loadLeads, [loadLeads]);

  useEffect(() => {
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="admin" style={{ minHeight: "60vh" }} />;
  if (!authed) {
    return (
      <div className="admin">
        <Seo title="Admin | Solar Black Belt" />
        <Login onAuthed={() => setAuthed(true)} />
      </div>
    );
  }
  return (
    <div className="admin">
      <Seo title="Admin | Solar Black Belt" />
      <div className="admin-top">
        <Link to="/admin" className="brand">
          <span className="brandmark"><LogoMark size={22} /></span>
          <span>SOLAR BLACK BELT<small>ADMIN · {total} LEADS</small></span>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/" className="textbutton">View site ↗</Link>
          <button className="btn small" data-testid="admin-logout" onClick={async () => { await api.post("/admin/logout").catch(() => {}); setAuthed(false); }}>
            Sign out
          </button>
        </div>
      </div>
      <div className="admin-body">
        {leadId ? (
          <LeadDetail reference={leadId} />
        ) : (
          <>
            <div className="tabbar" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button key={t} className={tab === t ? "active" : ""} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}>
                  {t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            {tab === "leads" && <LeadsTab leads={leads} q={q} setQ={setQ} reload={loadLeads} />}
            {tab === "audits" && <AuditsTab />}
            {tab === "roofs" && <RoofLookupTab />}
            {tab === "projects" && <ProjectsTab />}
            {tab === "reviews" && <ReviewsTab />}
            {tab === "content" && <ContentTab />}
            {tab === "notifications" && <NotificationsTab reload={loadLeads} />}
            {tab === "settings" && <SettingsTab reload={loadLeads} />}
            {tab === "apikeys" && <ApiKeysTab />}
            {tab === "requests" && <ServiceRequestsTab />}
            {tab === "jobs" && <JobApplicationsTab />}
            {tab === "pages" && <PagesTab />}
            {tab === "reports" && <ReportsTab />}
            {tab === "florida" && <FloridaTab />}
            {tab === "academy" && <AcademyTab />}
            {tab === "audit" && <AuditTab />}
          </>
        )}
      </div>
    </div>
  );
}
